import React, { useState, useEffect, useContext, useMemo } from "react";

import { UserContext } from "../../UserContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroups } from "../../store/groupsSlice";
import { fetchUsers } from "../../store/usersSlice";
import { getManagerGroups } from "../../api/groupsService";
import { openPopup } from "../Common/popupUIStore";
import {
   readGroupManagerId,
   readGroupManagerName,
   readGroupProfessorId,
   readGroupProfessorName,
   readGroupSector,
   readGroupToken,
   readStudentGroupId,
} from "../../utils/groupFields";
import { normalizeRole } from "../../auth/access";

import StudentItem from "../Common/StudentItem";
import { ButtonUI, GroupItemUI } from "../UI";
import { filterStudentsByBookingStatus } from "../../hooks/useBookingStatusMonitor";
import useAutoLoadMore from "../../hooks/useAutoLoadMore";
import {
   bookingStatusLabel,
   bookingStatusShortLabel,
   bookingStatusTitle,
   bookingStatusTone,
} from "../../utils/bookingStatus";

/* ===================== Utils ===================== */
const GROUPS_PAGE_SIZE = 10;



/* 🔒 token protejat = ABCD1234 */
const isProtectedToken = (token) =>
   String(token ?? "")
      .trim()
      .toUpperCase() === "ABCD1234";

const personName = (p) =>
   `${p?.firstName || ""} ${p?.lastName || ""}`.trim();

// fără starea programării (alt rol sau încă nu a venit de la server)
const NO_MONITOR = { enabled: false, byUser: new Map(), stateOf: () => null };

// Căutarea, filtrul după stare și starea programării vin din bara de sus a
// paginii (AdminHome), la fel „Grupă nouă”; onCount primește câte grupe se văd.
// Avatarele (grupa și membrii): AvatarUI, culoarea și inițialele după nume.
function GroupManager({
   query = "",
   statusFilter = null,
   bookingMonitor = NO_MONITOR,
   onCount,
}) {
   const { user } = useContext(UserContext);
   const dispatch = useDispatch();

   const groupsState = useSelector((state) => state.groups || {});
   const usersState = useSelector((state) => state.users || {});
   const groups = Array.isArray(groupsState.list) ? groupsState.list : [];
   const users = useMemo(() => {
      const merged = new Map();
      const fromGroups = Array.isArray(groupsState.users)
         ? groupsState.users
         : [];
      const fromUsers = Array.isArray(usersState.list) ? usersState.list : [];
      for (const u of [...fromGroups, ...fromUsers]) {
         const id = u?.id ?? u?.userId ?? u?.user?.id;
         if (id == null) continue;
         merged.set(String(id), u?.user && u.user.id != null ? u.user : u);
      }
      return Array.from(merged.values());
   }, [groupsState.users, usersState.list]);

   const visibleGroups = useMemo(
      () => groups.filter((g) => !isProtectedToken(readGroupToken(g))),
      [groups],
   );

   const managers = useMemo(
      () => users.filter((u) => normalizeRole(u?.role) === "MANAGER"),
      [users],
   );

   const usersById = useMemo(() => {
      const map = new Map();
      for (const u of users) {
         const person = u?.user && (u.user.id != null || u.user.userId != null)
            ? u.user
            : u;
         for (const key of [person?.id, person?.userId, u?.id, u?.userId]) {
            if (key != null && key !== "") map.set(String(key), person);
         }
      }
      return map;
   }, [users]);

   // Fallback când GET /groups nu include managerId / professorId.
   const [managerByGroupId, setManagerByGroupId] = useState({});
   const [assignmentByGroupId, setAssignmentByGroupId] = useState({});

   useEffect(() => {
      if (!managers.length) {
         setManagerByGroupId({});
         setAssignmentByGroupId({});
         return undefined;
      }

      let cancelled = false;
      (async () => {
         const entries = await Promise.all(
            managers.map(async (m) => {
               try {
                  const arr = await getManagerGroups(m.id);
                  return { manager: m, groups: arr };
               } catch {
                  return { manager: m, groups: [] };
               }
            }),
         );
         if (cancelled) return;
         const map = {};
         const extras = {};
         for (const { manager, groups: assigned } of entries) {
            for (const g of assigned) {
               const gid = String(g?.id ?? g?.groupId ?? "");
               if (!gid) continue;
               map[gid] = manager;
               extras[gid] = g;
            }
         }
         setManagerByGroupId(map);
         setAssignmentByGroupId(extras);
      })();

      return () => {
         cancelled = true;
      };
   }, [managers]);

   const [viewMode, setViewMode] = useState({ mode: "list", group: null });

   useEffect(() => {
      dispatch(fetchGroups());
      dispatch(fetchUsers());
   }, [dispatch, user?.id]);

   const groupedUsersByGroup = useMemo(() => {
      return visibleGroups.map((group) => {
         const extra = assignmentByGroupId[String(group.id)] || {};
         return {
            ...extra,
            ...group,
            professorId:
               group.professorId ?? extra.professorId ?? extra.professor?.id,
            managerId: group.managerId ?? extra.managerId,
            professor: group.professor ?? extra.professor,
            members: users.filter((u) => {
               const role = normalizeRole(u?.role);
               if (role && role !== "USER") return false;
               return String(readStudentGroupId(u) || "") === String(group.id);
            }),
         };
      });
   }, [visibleGroups, users, assignmentByGroupId]);

   // Căutarea din listă e după STUDENT: păstrăm doar grupele care conțin cel
   // puțin un student ce se potrivește (nume, telefon, email, IDNP). Grupa
   // ABCD1234 e deja exclusă din groupedUsersByGroup (token protejat).
   // Filtrul după stare se pune o singură dată, din lista de grupe: lista
   // arată doar grupele cu elevi în acea stare, iar la deschiderea unei grupe
   // membrii sunt deja filtrați.
   const groupsByStatus = useMemo(() => {
      if (!statusFilter) return groupedUsersByGroup;
      return groupedUsersByGroup
         .map((group) => {
            const matching = filterStudentsByBookingStatus(
               group.members || [],
               bookingMonitor.byUser,
               statusFilter,
            );
            return { ...group, matchingMembersCount: matching.length };
         })
         .filter((group) => group.matchingMembersCount > 0);
   }, [bookingMonitor, groupedUsersByGroup, statusFilter]);

   const filteredGroups = useMemo(() => {
      const q = String(query || "")
         .trim()
         .toLowerCase();
      if (!q) return groupsByStatus;

      return groupsByStatus.filter((group) =>
         (group.members || []).some((student) =>
            [
               student?.firstName,
               student?.lastName,
               student?.email,
               student?.phone,
               student?.idnp,
            ]
               .filter(Boolean)
               .join(" ")
               .toLowerCase()
               .includes(q),
         ),
      );
   }, [groupsByStatus, query]);

   const [visibleGroupCount, setVisibleGroupCount] = useState(GROUPS_PAGE_SIZE);

   useEffect(() => {
      setVisibleGroupCount(GROUPS_PAGE_SIZE);
   }, [query, statusFilter, viewMode.mode]);

   const pagedGroups = useMemo(
      () => filteredGroups.slice(0, visibleGroupCount),
      [filteredGroups, visibleGroupCount],
   );

   const totalGroupResults = filteredGroups.length;
   useEffect(() => {
      onCount?.(totalGroupResults);
   }, [onCount, totalGroupResults]);
   const canLoadMoreGroups =
      viewMode.mode === "list" && visibleGroupCount < totalGroupResults;

   // fără „Afișează încă”: următoarele vin singure aproape de capătul listei (jos, sau în
   // dreapta pe telefon, unde rândul se derulează orizontal)
   const autoLoad = useAutoLoadMore(canLoadMoreGroups, () =>
      setVisibleGroupCount((count) =>
         Math.max(count, Math.min(visibleGroupCount + GROUPS_PAGE_SIZE, totalGroupResults)),
      ),
   );

   const detailGroup =
      viewMode.mode === "details" && viewMode.group
         ? groupedUsersByGroup.find(
              (group) => String(group.id) === String(viewMode.group.id),
           ) || viewMode.group
         : null;

   const detailMembers = useMemo(
      () => detailGroup?.members ?? [],
      [detailGroup],
   );

   const filteredDetailMembers = useMemo(() => {
      const byStatus = filterStudentsByBookingStatus(
         detailMembers,
         bookingMonitor.byUser,
         statusFilter,
      );
      const q = String(query || "")
         .trim()
         .toLowerCase();

      if (!q) return byStatus;

      return byStatus.filter((student) =>
         [
            student?.firstName,
            student?.lastName,
            student?.email,
            student?.phone,
            student?.idnp,
         ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q),
      );
   }, [bookingMonitor, detailMembers, query, statusFilter]);

   // Dacă grupa curentă devine invizibilă (token protejat), revenim la listă.
   useEffect(() => {
      if (viewMode.mode !== "details" || !viewMode.group) return;
      const stillVisible = visibleGroups.some(
         (g) => String(g.id) === String(viewMode.group.id),
      );
      if (!stillVisible) setViewMode({ mode: "list", group: null });
   }, [viewMode, visibleGroups]);

   const handleOpenStudentPopup = (student) => {
      openPopup("studentDetails", { student });
   };

   const openGroupDetailsFromSearch = (group) => {
      if (!group) return;
      // Păstrăm căutarea curentă ca în grupă să apară doar studenții găsiți.
      setViewMode({ mode: "details", group });
   };

   const openGroupEditFromSearch = (group) => {
      if (!group) return;
      openPopup("groupEdit", { groupId: group.id, mode: "edit" });
   };


   return (
      <div className="studentsGroupsUI">
         <div className="studentsGroupsUI__header">
            <h2 className="studentsGroupsUI__title">Grupe</h2>
            {viewMode.mode === "details" && viewMode.group ? (
               // grupa deschisă: sus în dreapta, lângă titlu, înapoi la grupe și editarea grupei
               <div className="studentsGroupsUI__detailBar">
                  <ButtonUI icon="arrow" onClick={() => setViewMode({ mode: "list", group: null })}>
                     Înapoi
                  </ButtonUI>
                  <ButtonUI icon="edit" variant="info-soft" onClick={() => openGroupEditFromSearch(viewMode.group)}>
                     Editează
                  </ButtonUI>
               </div>
            ) : null}
         </div>

         <div className="studentsGroupsUI__gridWrap">
            <div className="studentsGroupsUI__grid" ref={autoLoad.ref} onScroll={autoLoad.onScroll}>
               {/* ===== LISTĂ GRUPE ===== */}
               {viewMode.mode === "list" &&
                  pagedGroups.map((group) => {
                     const pLabel =
                        readGroupProfessorName(group, usersById) || "—";

                     const mappedManager =
                        managerByGroupId[String(group.id)] || null;
                     const mLabel =
                        readGroupManagerName(group, usersById) ||
                        (mappedManager ? personName(mappedManager) : "") ||
                        "—";

                     const sectorLabel = readGroupSector(group);
                     const groupToken = readGroupToken(group);

                     return (
                        // caseta grupei (tiparul GroupItemUI): toată se apasă și deschide grupa
                        <GroupItemUI
                           key={group.id}
                           name={group.name}
                           token={groupToken}
                           highlightQuery={query}
                           onOpen={() => openGroupDetailsFromSearch(group)}
                           rows={[
                              [
                                 { icon: "graduate", label: pLabel, title: "Profesor", grow: true },
                                 { icon: "map", label: sectorLabel || "—", title: "Sector" },
                              ],
                              [
                                 { icon: "manager", label: mLabel, title: "Manager", grow: true },
                                 {
                                    icon: "group",
                                    label: statusFilter
                                       ? `${group.matchingMembersCount}/${group.members.length}`
                                       : String(group.members.length),
                                    title: statusFilter
                                       ? `${group.matchingMembersCount} din ${group.members.length} membri în starea aleasă`
                                       : "Membri",
                                 },
                              ],
                           ]}
                        />
                     );
                  })}

               {/* ===== STARE GOALĂ (listă) ===== */}
               {viewMode.mode === "list" && filteredGroups.length === 0 && (
                  <p className="studentsGroupsUI__empty">
                     {String(query || "").trim()
                        ? "Nicio grupă cu acest student."
                        : statusFilter
                          ? "Nicio grupă cu elevi în această stare."
                          : "Nu există grupe."}
                  </p>
               )}

               {/* ===== DETALII GRUP ===== */}
               {viewMode.mode === "details" && viewMode.group && (
                  <>
                     {filteredDetailMembers.length > 0 ? (
                        filteredDetailMembers.map((student, idx) => {
                           const key = String(
                              student.id ??
                                 student.phone ??
                                 student.email ??
                                 `__idx_${idx}`,
                           );
                           const bookingState = bookingMonitor.stateOf(student);

                           return (
                              <StudentItem
                                 key={student.id ?? key}
                                 student={student}
                                 onOpen={handleOpenStudentPopup}
                                 highlightQuery={query}
                                 secondaryText={student.phone || "–"}
                                 phoneCopy
                                 statusText={bookingStatusShortLabel(bookingState)}
                                 statusTone={bookingStatusTone(bookingState)}
                                 statusTitle={
                                    bookingState
                                       ? `${bookingStatusLabel(bookingState)} — ${bookingStatusTitle(bookingState)}`
                                       : ""
                                 }
                              />
                           );
                        })
                     ) : (
                        <p className="studentsGroupsUI__empty">
                           {statusFilter
                              ? "Niciun student cu această stare în grupă."
                              : String(query || "").trim()
                                ? "Nu există studenți care să se potrivească."
                                : "Nu sunt studenți în această grupă."}
                        </p>
                     )}
                  </>
               )}
            </div>
         </div>
      </div>
   );
}

export default GroupManager;
